import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Meetzy como personaje: una esfera con el degradado del icono de marca, un
 * borde de luz que recorre el contorno y dos ojos de cómic que parpadean y
 * miran alrededor.
 *
 * Por qué SVG animado y no una imagen generada: lo que da vida al personaje es
 * el movimiento —los ojos y el borde girando—, y un PNG no se mueve. Además
 * escala sin perder nitidez, pesa unos pocos cientos de bytes y no pasa por el
 * pipeline de assets.
 *
 * El degradado sale de muestrear el logo (`meetzy-logo.png`): lima #afe65b
 * arriba, verde #59a555 en el cuerpo y turquesa #12d9a9 abajo. Las paradas de
 * aquí son esos tonos subidos de saturación, porque sobre una esfera con
 * sombra los valores planos del logo se apagaban.
 *
 * **Claro y oscuro no comparten halo.** Una luz clara sobre papel claro
 * simplemente desaparece, así que el modo claro usa un halo más cerrado y con
 * menos alfa, y un borde MÁS saturado en lugar de más brillante. Los dos
 * juegos están como custom properties al principio del bloque de estilos.
 *
 * Capas, de fuera a dentro: halo ancho -> borde difuso -> borde estrecho ->
 * esfera. Los dos bordes giran despacio y en sentidos contrarios: es lo que
 * hace que la bola parezca emitir en vez de estar pintada.
 *
 * `mood`:
 *   'idle'   mira alrededor tranquilo (el estado por defecto)
 *   'happy'  ojos en arco, para cuando algo ha salido bien
 *
 * Los ids de los degradados llevan sufijo de instancia porque en SVG son
 * globales al documento: dos orbes en la misma página con el mismo id harían
 * que el segundo pintara con el degradado del primero.
 */
@Component({
  selector: 'app-meetzy-orb',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="mz-orb" [class.mz-orb--dark]="dark"
         [style.width.px]="size" [style.height.px]="size" [attr.aria-hidden]="true">
      <div class="mz-orb__rim mz-orb__rim--soft"></div>
      <div class="mz-orb__bloom"></div>
      <div class="mz-orb__rim"></div>

      <svg viewBox="0 0 120 120" class="mz-orb__svg">
        <defs>
          <!-- Diagonal y no radial: el logo va de lima arriba-izquierda a
               turquesa abajo-derecha, y un degradado radial lo aplanaba. -->
          <linearGradient [attr.id]="'mzOrbBody' + uid" x1="12%" y1="4%" x2="84%" y2="100%">
            <stop offset="0%" stop-color="#d3f36e" />
            <stop offset="34%" stop-color="#8ed85c" />
            <stop offset="66%" stop-color="#2ec488" />
            <stop offset="100%" stop-color="#04b28d" />
          </linearGradient>
          <radialGradient [attr.id]="'mzOrbShine' + uid" cx="34%" cy="26%" r="46%">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="0.42" />
            <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
          </radialGradient>
          <!-- Sombra interior abajo-derecha: convierte el círculo en esfera. -->
          <radialGradient [attr.id]="'mzOrbDepth' + uid" cx="66%" cy="82%" r="54%">
            <stop offset="0%" stop-color="#00402f" stop-opacity="0.34" />
            <stop offset="100%" stop-color="#00402f" stop-opacity="0" />
          </radialGradient>
          <!-- Luz en el canto de la propia esfera, por dentro: engancha el
               borde exterior con el cuerpo y evita que se vea pegado encima. -->
          <radialGradient [attr.id]="'mzOrbEdge' + uid" cx="50%" cy="50%" r="50%">
            <stop offset="82%" stop-color="#f2ffc9" stop-opacity="0" />
            <stop offset="96%" stop-color="#f2ffc9" stop-opacity="0.55" />
            <stop offset="100%" stop-color="#f2ffc9" stop-opacity="0.15" />
          </radialGradient>
        </defs>

        <g class="mz-orb__body">
          <circle cx="60" cy="60" r="52" [attr.fill]="'url(#mzOrbBody' + uid + ')'" />
          <circle cx="60" cy="60" r="52" [attr.fill]="'url(#mzOrbDepth' + uid + ')'" />
          <ellipse cx="44" cy="33" rx="24" ry="17" [attr.fill]="'url(#mzOrbShine' + uid + ')'" />
          <circle cx="60" cy="60" r="52" [attr.fill]="'url(#mzOrbEdge' + uid + ')'" />

          <g class="mz-orb__look">
            <g class="mz-orb__blink">
              <ng-container *ngIf="mood !== 'happy'">
                <rect x="37.5" y="47" width="18" height="18" rx="9" fill="#0b2019" />
                <rect x="64.5" y="47" width="18" height="18" rx="9" fill="#0b2019" />
                <!-- El destello es lo que separa "dos manchas" de "dos ojos". -->
                <circle cx="42.9" cy="52.9" r="3.1" fill="#ffffff" opacity="0.92" />
                <circle cx="69.9" cy="52.9" r="3.1" fill="#ffffff" opacity="0.92" />
              </ng-container>
              <ng-container *ngIf="mood === 'happy'">
                <path d="M38 61 q8.5 -14 17 0" fill="none" stroke="#0b2019" stroke-width="6.5" stroke-linecap="round" />
                <path d="M65 61 q8.5 -14 17 0" fill="none" stroke="#0b2019" stroke-width="6.5" stroke-linecap="round" />
              </ng-container>
            </g>
          </g>
        </g>
      </svg>
    </div>
  `,
  styles: [`
    .mz-orb {
      position: relative;
      display: inline-block;
      line-height: 0;

      /* Modo claro por defecto: halo más cerrado y con menos alfa, borde más
         saturado. Los tonos del modo oscuro sobre papel blanco desaparecen. */
      --bloom-1: rgba(40, 180, 110, .22);
      --bloom-2: rgba(6, 170, 150, .13);
      --bloom-3: rgba(5, 140, 170, .05);
      --bloom-inset: -34%;
      --rim: conic-gradient(from 0deg, #9fdc4a, #5cc95c, #12b078, #06b0ab, #0d93bf, #06b0ab, #12b078, #5cc95c, #9fdc4a);
      --rim-op: .5;
      --rim-soft-op: .32;
    }

    .mz-orb--dark {
      --bloom-1: rgba(124, 228, 144, .32);
      --bloom-2: rgba(16, 205, 175, .19);
      --bloom-3: rgba(10, 185, 205, .08);
      --bloom-inset: -46%;
      --rim: conic-gradient(from 0deg, #eaff9e, #a8e85f, #3fd08a, #0ad0c8, #19b9dd, #0ad0c8, #3fd08a, #a8e85f, #eaff9e);
      --rim-op: .42;
      --rim-soft-op: .3;
    }

    .mz-orb__svg {
      position: relative;
      z-index: 3;
      width: 100%;
      height: 100%;
      overflow: visible;
      display: block;
    }

    .mz-orb__bloom {
      position: absolute;
      inset: var(--bloom-inset);
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
      background: radial-gradient(circle, var(--bloom-1) 0%, var(--bloom-2) 34%, var(--bloom-3) 55%, transparent 74%);
      animation: mzOrbBreathe 6s ease-in-out infinite;
    }

    /* Anillo estrecho pegado a la silueta. Gira despacio: el color recorre el
       contorno y la bola parece emitir en vez de estar pintada. */
    .mz-orb__rim {
      position: absolute;
      inset: -3.5%;
      border-radius: 50%;
      pointer-events: none;
      z-index: 2;
      background: var(--rim);
      opacity: var(--rim-op);
      -webkit-mask: radial-gradient(circle, transparent 47.5%, #000 51%, #000 54%, transparent 63%);
      mask: radial-gradient(circle, transparent 47.5%, #000 51%, #000 54%, transparent 63%);
      filter: blur(4px);
      animation: mzOrbSpin 26s linear infinite;
    }

    /* El mismo anillo, más ancho y muy difuso, girando al revés: da la
       vibración espectral sin que se lea como una segunda línea. */
    .mz-orb__rim--soft {
      inset: -11%;
      z-index: 1;
      opacity: var(--rim-soft-op);
      filter: blur(12px);
      -webkit-mask: radial-gradient(circle, transparent 42%, #000 50%, transparent 66%);
      mask: radial-gradient(circle, transparent 42%, #000 50%, transparent 66%);
      animation: mzOrbSpin 38s linear infinite reverse, mzOrbBreathe 7s ease-in-out infinite;
    }

    .mz-orb__body {
      transform-box: fill-box;
      transform-origin: center;
      animation: mzOrbFloat 4.5s ease-in-out infinite;
    }

    /* Dos grupos anidados porque las dos animaciones son transforms y una sola
       propiedad no puede llevar las dos: fuera la mirada, dentro el parpadeo. */
    .mz-orb__look {
      transform-box: fill-box;
      transform-origin: center;
      animation: mzOrbLook 9s ease-in-out infinite;
    }

    .mz-orb__blink {
      transform-box: fill-box;
      transform-origin: center;
      animation: mzOrbBlink 5.2s ease-in-out infinite;
    }

    @keyframes mzOrbSpin { to { transform: rotate(360deg); } }

    @keyframes mzOrbBreathe {
      0%, 100% { opacity: .82; transform: scale(1); }
      50%      { opacity: 1;   transform: scale(1.05); }
    }

    @keyframes mzOrbFloat {
      0%, 100% { transform: translateY(0) scale(1); }
      50%      { transform: translateY(-5px) scale(1.015); }
    }

    /* Parpadeo doble al final del ciclo: uno solo se lee como un fallo de
       render, dos seguidos se leen como un ser vivo. */
    @keyframes mzOrbBlink {
      0%, 86%, 100% { transform: scaleY(1); }
      88%           { transform: scaleY(0.06); }
      90%           { transform: scaleY(1); }
      93%           { transform: scaleY(0.06); }
      95%           { transform: scaleY(1); }
    }

    @keyframes mzOrbLook {
      0%, 26%   { transform: translateX(0) translateY(0); }
      34%, 52%  { transform: translateX(5px) translateY(-1.5px); }
      60%, 80%  { transform: translateX(-4px) translateY(1.5px); }
      88%, 100% { transform: translateX(0) translateY(0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .mz-orb__bloom,
      .mz-orb__rim,
      .mz-orb__body,
      .mz-orb__look,
      .mz-orb__blink { animation: none; }
    }
  `],
})
export class MeetzyOrbComponent {
  @Input() size = 104;
  @Input() mood: 'idle' | 'happy' = 'idle';

  /** El halo y el borde se calibran distinto por tema; ver el bloque de estilos. */
  @Input() dark = false;

  /** Sufijo único por instancia: los ids de SVG son globales al documento. */
  readonly uid = Math.random().toString(36).slice(2, 8);
}
